import { useEffect } from "react";
import { type Resolver, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { FormSelect } from "@/components/ui/forms/formSelectComponent/formSelect.component";
import { FormDateRangePreset } from "@/components/ui/forms/formDateRangePreset/formDateRangePreset.component";
import { DATE_PRESETS } from "@/utils/constants";
import { presetToRangeDate } from "@/utils/utils";
import type { FiltroAuditoriaAplicado, FiltroTipoAuditoria } from "../../../../cadastroRestricoes.types";
import { OPCOES_TIPO } from "../auditoria.utils";
import type { FiltrosAuditoriaForm } from "../auditoria.types";

type FiltrosAuditoriaProps = {
	readonly aberto: boolean;
	readonly carregando: boolean;
	readonly onPesquisar: (filtros: FiltroAuditoriaAplicado) => void;
};

const schema = z.object({
	tipo: z.enum(["todos", "salvar_acessos_grupo_usuario", "clonar_grupo_usuario"]),
	periodo: z.tuple([z.string().nullable(), z.string().nullable()]),
	periodo_preset: z.string(),
});

const getDefaultValues = (): FiltrosAuditoriaForm => {
	const [inicio, fim] = presetToRangeDate("thisMonth");
	return {
		tipo: "todos",
		periodo: [inicio, fim],
		periodo_preset: "thisMonth",
	};
};

/** Filtros da auditoria (RHF + Zod): tipo + período (presets/De-Até) aplicados via "Pesquisar". */
export default function FiltrosAuditoria({ aberto, carregando, onPesquisar }: FiltrosAuditoriaProps) {
	const { control, handleSubmit, setValue, reset } = useForm<FiltrosAuditoriaForm>({
		defaultValues: getDefaultValues(),
		resolver: zodResolver(schema) as unknown as Resolver<FiltrosAuditoriaForm>,
		mode: "onChange",
	});

	// Reseta os filtros sempre que o modal abre (novo grupo / nova consulta).
	useEffect(() => {
		if (aberto) reset(getDefaultValues());
	}, [aberto, reset]);

	const onSubmit = (valores: FiltrosAuditoriaForm) =>
		onPesquisar({
			tipo: valores.tipo,
			dataInicio: valores.periodo[0] ?? "",
			dataFim: valores.periodo[1] ?? "",
		});

	return (
		<section className="shrink-0 rounded-md border border-border/60 bg-card p-3.5 shadow-sm">
			<div className="mb-4 flex items-center gap-2">
				<span className="flex shrink-0 items-center justify-center text-primary">
					<FuseSvgIcon size={18}>lucide:funnel</FuseSvgIcon>
				</span>

				<div className="min-w-0">
					<h2 className="text-sm font-bold leading-5 text-foreground">Filtros</h2>
					<p className="text-xs font-medium leading-4 text-muted-foreground">
						Refine o histórico por tipo de evento e período.
					</p>
				</div>
			</div>

			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2 sm:flex-row sm:items-start -my-4">
				<FormSelect<FiltrosAuditoriaForm, FiltroTipoAuditoria>
					control={control}
					name="tipo"
					label="Tipo"
					options={OPCOES_TIPO}
					className="w-full sm:mt-6.5 sm:w-56"
					onValueChange={() => handleSubmit(onSubmit)()}
				/>

				<FormDateRangePreset<FiltrosAuditoriaForm>
					control={control}
					setValue={setValue}
					name="periodo"
					presetName="periodo_preset"
					label="Período"
					presets={DATE_PRESETS}
					className="w-full sm:flex-1"
				/>

				<Button
					type="submit"
					variant="capsule"
					color="primary"
					size="small"
					disabled={carregando}
					startIcon={<FuseSvgIcon size={16}>lucide:search</FuseSvgIcon>}
					className="w-full sm:mt-6.5 sm:w-auto"
				>
					Pesquisar
				</Button>
			</form>
		</section>
	);
}
